import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminPanel.css';

const sampleUsers = [
	{
		__backendId: 'u1',
		nombre: 'Juan',
		apellido: 'Pérez',
		tipo: 'particular',
		rut_personal: '12.345.678-9',
		correo: 'juan@example.com',
		direccion: 'Calle Falsa 123',
		ciudad: 'Santiago',
		region: 'Región Metropolitana',
		fecha_registro: new Date().toISOString()
	},
	{
		__backendId: 'u2',
		nombre: 'María',
		apellido: 'Gómez',
		tipo: 'empresa',
		razon_social: 'MG Servicios SPA',
		rut_empresa: '76.123.456-7',
		direccion_empresa: 'Av. Comercio 45',
		correo: 'contacto@mgservicios.cl',
		fecha_registro: new Date().toISOString()
	},
	{
		__backendId: 'u3',
		nombre: 'Ana',
		apellido: 'López',
		tipo: 'particular',
		rut_personal: '11.222.333-4',
		correo: 'ana.lopez@example.com',
		direccion: 'Pasaje Real 9',
		ciudad: 'Valparaíso',
		region: 'Valparaíso',
		fecha_registro: new Date().toISOString()
	}
];

const sampleCodes = [
	{
		__backendId: 'c1',
		codigo: 'ABC123DEF456GHI7',
		email_cliente: 'cliente1@example.com',
		estado: 'activo',
		fecha_registro: new Date().toISOString()
	}
];

function formatDate(d) {
	try {
		return new Date(d).toLocaleString('es-CL');
	} catch (e) {
		return d;
	}
}

export default function AdminPanel() {
	const navigate = useNavigate();
	const [tab, setTab] = useState('usuarios');
	const [users, setUsers] = useState(sampleUsers);
	const [codes, setCodes] = useState(sampleCodes);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedCode, setSelectedCode] = useState(null);

	const [showUserModal, setShowUserModal] = useState(false);
	const [showCodeModal, setShowCodeModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null); // { type, id }
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [toasts, setToasts] = useState([]);

	const [newUserTipo, setNewUserTipo] = useState('');
	const [generatedCode, setGeneratedCode] = useState('');
	const [deleteConfirmText, setDeleteConfirmText] = useState('');

	useEffect(() => {
		// keep selection valid when users/codes change
		if (selectedUser && !users.find(u => u.__backendId === selectedUser.__backendId)) {
			setSelectedUser(null);
		}
		if (selectedCode && !codes.find(c => c.__backendId === selectedCode.__backendId)) {
			setSelectedCode(null);
		}
	}, [users, codes]);

	function showToast(message, type = 'info') {
		const id = Date.now().toString();
		setToasts(t => [...t, { id, message, type }]);
		setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
	}

	function generateRandomCode() {
		const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		let code = '';
		for (let i = 0; i < 16; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
		return code;
	}

	function handleSelectUser(user) {
		setSelectedUser(user);
		setSelectedCode(null);
	}

	function handleSelectCode(code) {
		setSelectedCode(code);
		setSelectedUser(null);
	}

	async function handleNewUserSubmit(e) {
		e.preventDefault();
		const fd = new FormData(e.target);
		const tipo = fd.get('tipo') || '';
		if (!tipo) return showToast('Selecciona un tipo de usuario', 'error');
		const newUser = {
			__backendId: 'u' + Date.now(),
			tipo,
			nombre: fd.get('nombre') || '',
			apellido: fd.get('apellido') || '',
			rut_personal: fd.get('rut_personal') || '',
			correo: fd.get('correo') || '',
			direccion: fd.get('direccion') || '',
			ciudad: fd.get('ciudad') || '',
			region: fd.get('region') || '',
			razon_social: fd.get('razon_social') || '',
			rut_empresa: fd.get('rut_empresa') || '',
			direccion_empresa: fd.get('direccion_empresa') || '',
			password: fd.get('password') || '',
			fecha_registro: new Date().toISOString()
		};
		setUsers(u => [newUser, ...u]);
		setShowUserModal(false);
		showToast('Usuario creado exitosamente', 'success');
	}

	async function handleNewCodeSubmit(e) {
		e.preventDefault();
		const fd = new FormData(e.target);
		const code = (fd.get('codigo') || '').trim();
		const email = (fd.get('email') || '').trim();
		if (!code || code.length !== 16) return showToast('El código debe tener 16 caracteres', 'error');
		if (!email) return showToast('Ingresa un correo válido', 'error');
		if (codes.find(c => c.codigo === code)) return showToast('Este código ya existe', 'error');
		const newCode = {
			__backendId: 'c' + Date.now(),
			codigo: code,
			email_cliente: email,
			estado: 'activo',
			fecha_registro: new Date().toISOString()
		};
		setCodes(c => [newCode, ...c]);
		setShowCodeModal(false);
		showToast(`Código creado y listo para enviar a ${email}`, 'success');
	}

	function openDeleteModal(target) {
		setDeleteTarget(target);
		setDeleteConfirmText('');
		setShowDeleteModal(true);
	}

	function confirmDelete() {
		if (deleteConfirmText !== 'ELIMINAR') return showToast('Debes escribir ELIMINAR para confirmar', 'error');
		if (!deleteTarget) return;
		if (deleteTarget.type === 'user') {
			setUsers(u => u.filter(x => x.__backendId !== deleteTarget.id));
			setSelectedUser(null);
			showToast('Usuario eliminado', 'success');
		} else if (deleteTarget.type === 'code') {
			setCodes(c => c.filter(x => x.__backendId !== deleteTarget.id));
			setSelectedCode(null);
			showToast('Código eliminado', 'success');
		}
		setShowDeleteModal(false);
	}

	function handleChangePassword(e) {
		e.preventDefault();
		const fd = new FormData(e.target);
		const newP = fd.get('newPassword') || '';
		const confirm = fd.get('confirmPassword') || '';
		if (newP.length < 6) return showToast('La contraseña debe tener al menos 6 caracteres', 'error');
		if (newP !== confirm) return showToast('Las contraseñas no coinciden', 'error');
		if (!selectedUser) return;
		setUsers(u => u.map(x => x.__backendId === selectedUser.__backendId ? { ...x, password: newP } : x));
		setShowPasswordModal(false);
		showToast('Contraseña actualizada', 'success');
	}

	return (
		<div className="admin-panel">
			<div className="admin-container">
				<header className="admin-header">
					<div className="header-inner">
						<div className="brand">
							<div className="brand-icon">KER</div>
							<div>
								<h1 className="panel-title">Panel de Administración</h1>
								<p className="panel-sub">Gestión de usuarios KER</p>
							</div>
						</div>
						<div className="header-actions">
							<button className="btn-secondary nav-btn" onClick={() => navigate('/dashboard')}>
								<svg className="icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path strokeWidth="2" d="M3 13h8V3H3zm10 8h8v-6h-8zM13 3v8h8V3zM3 21h8v-6H3z"/></svg>
								Ir al Dashboard
							</button>
							<button className="btn-primary new-btn" onClick={() => { setShowUserModal(true); setNewUserTipo(''); }}>
								<svg className="icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
								Nuevo Usuario
							</button>
						</div>
					</div>
				</header>

				<div className="tabs">
					<button className={`tab-btn ${tab === 'usuarios' ? 'active' : ''}`} onClick={() => setTab('usuarios')}>
						<svg className="tab-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M7 20H2v-2a3 3 0 015.356-1.857"/></svg>
						Usuarios
						<span className="count-badge">{users.length}</span>
					</button>
					<button className={`tab-btn ${tab === 'codigos' ? 'active' : ''}`} onClick={() => setTab('codigos')}>
						<svg className="tab-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"><path strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
						Códigos de Registro
						<span className="count-badge">{codes.length}</span>
					</button>
				</div>

				<div className="main-grid">
					<div className="list-column">
						<div className="card list-card">
							<div className="list-header">
								<h2>Lista</h2>
							</div>
							<div className="list-scroll scrollbar-custom">
								{tab === 'usuarios' && (
									<>
										{users.length === 0 ? (
											<div className="empty-state">No hay usuarios registrados</div>
										) : (
											users.map(u => (
												<div key={u.__backendId} className={`user-card ${selectedUser && selectedUser.__backendId === u.__backendId ? 'active' : ''}`} onClick={() => handleSelectUser(u)}>
													<div className="user-left">
														<div className="avatar">{u.nombre.charAt(0)}</div>
														<div className="user-meta">
															<div className="user-name">{u.nombre} {u.apellido}</div>
															<div className="user-badge"><span className={`badge ${u.tipo === 'empresa' ? 'badge-empresa' : 'badge-particular'}`}>{u.tipo === 'empresa' ? '🏢 Empresa' : '👤 Particular'}</span></div>
														</div>
													</div>
													<div className="user-arrow">›</div>
												</div>
											))
										)}
									</>
								)}

								{tab === 'codigos' && (
									<>
										{codes.length === 0 ? (
											<div className="empty-state">No hay códigos generados</div>
										) : (
											codes.map(c => (
												<div key={c.__backendId} className={`user-card ${selectedCode && selectedCode.__backendId === c.__backendId ? 'active' : ''}`} onClick={() => handleSelectCode(c)}>
													<div className="user-left">
														<div className="avatar">#</div>
														<div className="user-meta">
															<div className="user-name mono">{c.codigo}</div>
															<div className="user-badge"><span className={`badge ${c.estado === 'activo' ? 'badge-particular' : ''}`}>{c.estado === 'activo' ? '✓ Activo' : '✓ Usado'}</span></div>
														</div>
													</div>
													<div className="user-arrow">›</div>
												</div>
											))
										)}
									</>
								)}
							</div>
						</div>
					</div>

					<div className="detail-column">
						{tab === 'usuarios' && !selectedUser && (
							<div className="card welcome-screen">
								<div className="welcome-inner">
									<div className="welcome-icon">👥</div>
									<h2>Bienvenido al Panel</h2>
									<p>Selecciona un usuario de la lista o crea uno nuevo para comenzar</p>
									<div className="stats-grid">
										<div className="stat"> <div className="stat-value">{users.length}</div> <div className="stat-label">Total Usuarios</div> </div>
										<div className="stat"> <div className="stat-value">{users.filter(u=>u.tipo==='empresa').length}</div> <div className="stat-label">Empresas</div> </div>
										<div className="stat"> <div className="stat-value">{users.filter(u=>u.tipo==='particular').length}</div> <div className="stat-label">Particulares</div> </div>
										<div className="stat"> <div className="stat-value">🔐</div> <div className="stat-label">Seguro</div> </div>
									</div>
								</div>
							</div>
						)}

						{tab === 'usuarios' && selectedUser && (
							<div className="card user-detail">
								<div className="detail-header">
									<div className="detail-left">
										<div className="detail-avatar">{selectedUser.nombre.charAt(0)}</div>
										<div>
											<h2 className="detail-name">{selectedUser.nombre} {selectedUser.apellido}</h2>
											<div className="detail-type"><span className={`badge ${selectedUser.tipo === 'empresa' ? 'badge-empresa' : 'badge-particular'}`}>{selectedUser.tipo === 'empresa' ? '🏢 Empresa' : '👤 Particular'}</span></div>
										</div>
									</div>
									<div>
										<button className="icon-btn" onClick={() => setSelectedUser(null)}>✕</button>
									</div>
								</div>

								<div className="detail-body">
									<div className="detail-section">
										<h3>Datos Personales</h3>
										<div className="grid-2">
											<div>
												<p className="muted">Nombre</p>
												<p className="mono">{selectedUser.nombre}</p>
											</div>
											<div>
												<p className="muted">Apellido</p>
												<p className="mono">{selectedUser.apellido}</p>
											</div>
											<div>
												<p className="muted">RUT</p>
												<p className="mono">{selectedUser.rut_personal || '-'}</p>
											</div>
											<div>
												<p className="muted">Correo</p>
												<p className="mono">{selectedUser.correo || '-'}</p>
											</div>
										</div>
									</div>

									{selectedUser.tipo === 'empresa' ? (
										<div className="detail-section">
											<h3>Datos de la Empresa</h3>
											<div className="grid-2">
												<div>
													<p className="muted">Razón Social</p>
													<p className="mono">{selectedUser.razon_social || '-'}</p>
												</div>
												<div>
													<p className="muted">RUT Empresa</p>
													<p className="mono">{selectedUser.rut_empresa || '-'}</p>
												</div>
												<div className="full">
													<p className="muted">Dirección</p>
													<p className="mono">{selectedUser.direccion_empresa || '-'}</p>
												</div>
											</div>
										</div>
									) : (
										<div className="detail-section">
											<h3>Ubicación</h3>
											<div className="grid-2">
												<div className="full">
													<p className="muted">Dirección</p>
													<p className="mono">{selectedUser.direccion || '-'}</p>
												</div>
												<div>
													<p className="muted">Ciudad</p>
													<p className="mono">{selectedUser.ciudad || '-'}</p>
												</div>
												<div>
													<p className="muted">Región</p>
													<p className="mono">{selectedUser.region || '-'}</p>
												</div>
											</div>
										</div>
									)}

									<div className="detail-section">
										<h3>Información de Registro</h3>
										<div className="grid-2">
											<div>
												<p className="muted">Fecha de Registro</p>
												<p className="mono">{formatDate(selectedUser.fecha_registro)}</p>
											</div>
											<div>
												<p className="muted">ID</p>
												<p className="mono small">{selectedUser.__backendId}</p>
											</div>
										</div>
									</div>

									<div className="actions-row">
										<button className="btn-primary" onClick={() => setShowPasswordModal(true)}>Cambiar Contraseña</button>
										<button className="btn-danger" onClick={() => openDeleteModal({ type: 'user', id: selectedUser.__backendId })}>Eliminar Usuario</button>
									</div>
								</div>
							</div>
						)}

						{tab === 'codigos' && !selectedCode && (
							<div className="card welcome-screen">
								<div className="welcome-inner">
									<div className="welcome-icon">🔐</div>
									<h2>Control de Acceso</h2>
									<p>Genera códigos de invitación para controlar quien se registra en tu plataforma</p>
									<div className="info-box">
										<ol>
											<li>Genera un código aleatorio</li>
											<li>Envía por correo a tu cliente</li>
											<li>El cliente usa el código para registrarse</li>
											<li>El código se marca como usado</li>
										</ol>
									</div>
									<div className="mt-4">
										<button className="btn-primary" onClick={() => { setShowCodeModal(true); setGeneratedCode(''); }}>Nuevo Código de Registro</button>
									</div>
								</div>
							</div>
						)}

						{tab === 'codigos' && selectedCode && (
							<div className="card user-detail">
								<div className="detail-header">
									<div className="detail-left">
										<div className="detail-avatar">#</div>
										<div>
											<h2 className="detail-name">Código de Registro</h2>
											<p className="muted">{selectedCode.estado === 'activo' ? '✓ Código Activo' : '✓ Código Usado'}</p>
										</div>
									</div>
									<div>
										<button className="icon-btn" onClick={() => setSelectedCode(null)}>✕</button>
									</div>
								</div>
								<div className="detail-body">
									<div className="detail-section">
										<h3>Código</h3>
										<div className="code-row">
											<code className="code-value">{selectedCode.codigo}</code>
											<button className="btn-primary" onClick={() => { navigator.clipboard?.writeText(selectedCode.codigo); showToast('Código copiado al portapapeles', 'success'); }}>Copiar</button>
										</div>
									</div>
									<div className="detail-section">
										<h3>Información del Cliente</h3>
										<p className="muted">Correo</p>
										<p className="mono">{selectedCode.email_cliente}</p>
										<div className="grid-2 mt-2">
											<div>
												<p className="muted">Fecha de Creación</p>
												<p className="mono">{formatDate(selectedCode.fecha_registro)}</p>
											</div>
											<div>
												<p className="muted">Estado</p>
												<p className="mono">{selectedCode.estado}</p>
											</div>
										</div>
									</div>
									<div className="actions-row">
										<button className="btn-danger" onClick={() => openDeleteModal({ type: 'code', id: selectedCode.__backendId })}>Eliminar Código</button>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Modals */}
				{showUserModal && (
					<div className="modal-overlay">
						<div className="modal-card">
							<div className="modal-header">
								<h3>Nuevo Usuario</h3>
								<button className="icon-btn" onClick={() => setShowUserModal(false)}>✕</button>
							</div>
							<form onSubmit={handleNewUserSubmit} className="modal-form">
								<label>Tipo de Usuario *</label>
								<select name="tipo" value={newUserTipo} onChange={e => setNewUserTipo(e.target.value)} required>
									<option value="">Seleccionar tipo</option>
									<option value="particular">Usuario Particular</option>
									<option value="empresa">Empresa</option>
								</select>

								<div className="grid-2">
									<div>
										<label>Nombre *</label>
										<input name="nombre" required />
									</div>
									<div>
										<label>Apellido *</label>
										<input name="apellido" required />
									</div>
								</div>

								<div className="grid-2">
									<div>
										<label>RUT Personal *</label>
										<input name="rut_personal" placeholder="12.345.678-9" required />
									</div>
									<div>
										<label>Correo Electrónico *</label>
										<input name="correo" type="email" required />
									</div>
								</div>

								{newUserTipo === 'particular' && (
									<>
										<div>
											<label>Dirección</label>
											<input name="direccion" />
										</div>
										<div className="grid-2">
											<div>
												<label>Ciudad</label>
												<input name="ciudad" />
											</div>
											<div>
												<label>Región</label>
												<input name="region" />
											</div>
										</div>
									</>
								)}

								{newUserTipo === 'empresa' && (
									<>
										<div className="grid-2">
											<div>
												<label>Razón Social</label>
												<input name="razon_social" />
											</div>
											<div>
												<label>RUT Empresa</label>
												<input name="rut_empresa" />
											</div>
										</div>
										<div>
											<label>Dirección Empresa</label>
											<input name="direccion_empresa" />
										</div>
									</>
								)}

								<div>
									<label>Contraseña *</label>
									<input name="password" type="password" required />
								</div>

								<div className="modal-actions">
									<button type="button" className="btn-cancel" onClick={() => setShowUserModal(false)}>Cancelar</button>
									<button type="submit" className="btn-primary">Guardar Usuario</button>
								</div>
							</form>
						</div>
					</div>
				)}

				{showCodeModal && (
					<div className="modal-overlay">
						<div className="modal-card">
							<div className="modal-header">
								<h3>Nuevo Código de Registro</h3>
								<button className="icon-btn" onClick={() => setShowCodeModal(false)}>✕</button>
							</div>
							<form onSubmit={handleNewCodeSubmit} className="modal-form">
								<label>Código (16 caracteres) *</label>
								<div className="code-generate">
									<input name="codigo" defaultValue={generatedCode} required />
									<button type="button" className="btn-primary" onClick={() => setGeneratedCode(generateRandomCode())}>Generar</button>
								</div>
								<label>Correo del Cliente *</label>
								<input name="email" type="email" placeholder="cliente@ejemplo.com" required />

								<div className="modal-actions">
									<button type="button" className="btn-cancel" onClick={() => setShowCodeModal(false)}>Cancelar</button>
									<button type="submit" className="btn-primary">Enviar Código</button>
								</div>
							</form>
						</div>
					</div>
				)}

				{showDeleteModal && (
					<div className="modal-overlay">
						<div className="modal-card">
							<div className="modal-header">
								<h3>Confirmar Eliminación</h3>
								<button className="icon-btn" onClick={() => setShowDeleteModal(false)}>✕</button>
							</div>
							<div className="modal-form">
								<p>Para confirmar, escribe <strong>ELIMINAR</strong></p>
								<input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
								<div className="modal-actions">
									<button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
									<button className="btn-danger" onClick={confirmDelete}>Eliminar</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{showPasswordModal && (
					<div className="modal-overlay">
						<div className="modal-card">
							<div className="modal-header">
								<h3>Cambiar Contraseña</h3>
								<button className="icon-btn" onClick={() => setShowPasswordModal(false)}>✕</button>
							</div>
							<form onSubmit={handleChangePassword} className="modal-form">
								<label>Nueva Contraseña *</label>
								<input name="newPassword" type="password" required />
								<label>Confirmar Contraseña *</label>
								<input name="confirmPassword" type="password" required />
								<div className="modal-actions">
									<button type="button" className="btn-cancel" onClick={() => setShowPasswordModal(false)}>Cancelar</button>
									<button type="submit" className="btn-primary">Cambiar Contraseña</button>
								</div>
							</form>
						</div>
					</div>
				)}

				{loading && (
					<div className="modal-overlay">
						<div className="loading-box">Procesando...</div>
					</div>
				)}

				{/* Toasts */}
				<div className="toasts">
					{toasts.map(t => (
						<div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
					))}
				</div>
			</div>
		</div>
	);
}

